import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Airports, AirportModel } from '@models/index';

export type ReservationIdentifierType = 'PNR' | 'TICKET' | 'INVALID';

export function sanitizePnrInput(value?: string | null): string {
  return (value ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

export function getIdentifierType(value?: string | null): ReservationIdentifierType {
  const normalized = sanitizePnrInput(value);

  if (/^[A-Z0-9]{6}$/.test(normalized)) {
    return 'PNR';
  }

  if (/^\d{13}$/.test(normalized)) {
    return 'TICKET';
  }

  return 'INVALID';
}

export function getReservationLookupErrorMsg(
  value?: string | null,
  options?: {
    criteria?: 'PNR' | 'TICKET';
    departureAirport?: string | null;
    checkDepartureAirport?: boolean;
  }
): string | null {
  const normalized = sanitizePnrInput(value);
  const identifierType = getIdentifierType(normalized);
  const criteria = options?.criteria ?? 'PNR';

  const rules: Array<{ when: boolean; msg: string }> = [
    {
      when: criteria === 'TICKET' && identifierType !== 'TICKET',
      msg: 'format error',
    },
    {
      when: identifierType === 'INVALID',
      msg: 'format error',
    },
    {
      when: identifierType === 'PNR' && PNRValidation.matchesAirportOrCity(normalized),
      msg: 'matches airport or city',
    },
    {
      when: !!options?.checkDepartureAirport && !options.departureAirport?.trim(),
      msg: 'missing departure airport',
    },
  ];

  return rules.find((rule) => rule.when)?.msg ?? null;
}

export function validatePnrInput(value?: string | null): boolean {
  return getReservationLookupErrorMsg(value) === null;
}

export default class PNRValidation {
  static PNRmatch(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const msg = getReservationLookupErrorMsg(control.value);
      return msg ? { pnr: { value: control.value, msg } } : null;
    };
  }

  static onlyLetters(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s'-]+$/;
      if (!regex.test(control.value.toString())) {
        return { onlyLetters: { value: control.value, msg: 'only letters allowed' } };
      }
      return null;
    };
  }

  static matchesAirportOrCity(value: string): boolean {
    const airports = Airports.getAirports();
    if (!airports || airports.length === 0) return false;

    const normalized = value
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    return airports.some((airport: AirportModel) => {
      if (airport.code && airport.code.toUpperCase() === normalized) return true;

      const cityNames = [
        airport.es_MX,
        airport.en_US,
        airport.fr_FR,
        airport.ja_JP,
        airport.kr_KR,
        airport.zh_CN,
        airport.du_NL,
        airport.pt_PT,
      ];

      return cityNames.some((name) => {
        if (!name) return false;
        const normalizedCity = name
          .toUpperCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .trim();
        return normalizedCity === normalized;
      });
    });
  }
}
